import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ChevronRight,
  Calendar,
  AlertCircle,
  Check,
  Clock,
  Search,
  X,
  MapPin,
} from "lucide-react";
import apiClient from "../apiClient";
import "./MITCheckinDashboard.css";

// Date utility functions
const formatDate = (date, format) => {
  const d = new Date(date);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  if (format === "MMM d, yyyy") {
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  } else if (format === "MMM d") {
    return `${months[d.getMonth()]} ${d.getDate()}`;
  } else if (format === "yyyy-MM-dd") {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return d.toLocaleDateString();
};

const addWeeks = (date, weeks) => {
  const d = new Date(date);
  d.setDate(d.getDate() + weeks * 7);
  return d;
};

const differenceInDays = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = d1 - d2;
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

// Helper function to get overall MIT status
const getOverallStatus = (mit) => {
  const currentWeek = mit.current_week;
  const overdueCheckins = mit.checkins.filter(
    (c) => c.status === "pending" && new Date(c.scheduled_date) < new Date()
  );

  if (overdueCheckins.length > 0) return "overdue";
  if (
    mit.checkins.some(
      (c) => c.status === "pending" && c.week_number <= currentWeek
    )
  )
    return "due_today";
  if (mit.checkins.filter((c) => c.status === "completed").length === 8)
    return "completed";
  return "upcoming";
};

// Helper function to get week status
const getWeekStatus = (startDate, checkin) => {
  if (checkin?.status === "completed") return "completed";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);

  const daysDiff = differenceInDays(today, startDate);

  if (checkin?.status === "pending" && daysDiff > 7) return "overdue";
  if (daysDiff >= 0 && daysDiff <= 7) return "due_today";
  return "upcoming";
};

// Main MIT List Component
const MITCheckinDashboard = () => {
  const [mits, setMits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMIT, setSelectedMIT] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  const fetchMITs = useCallback(async () => {
    try {
      const response = await apiClient.get("/api/mit-checkin/mits");

      if (response.data.success) {
        // Transform the data to match our component structure
        const transformedData = response.data.data.map((mit) => ({
          mit_user_id: mit.user_id,
          full_name: mit.full_name,
          store_name: mit.store_name,
          store_code: mit.location_pc,
          needs_location: mit.needs_location,
          corp: mit.corp,
          market: mit.market,
          start_date: mit.hire_date,
          current_week: mit.current_week,
          checkins: mit.checkins || [],
          current_status: getOverallStatus(mit),
        }));

        setMits(transformedData);
      }
    } catch (error) {
      console.error("Error fetching MITs:", error);
      showNotification("Failed to load MITs", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMITs();
  }, [fetchMITs]);

  const showNotification = (message, type = "info") => {
    setNotification({ show: true, message, type });
    setTimeout(
      () => setNotification({ show: false, message: "", type: "" }),
      5000
    );
  };

  const filteredMITs = mits.filter(
    (mit) =>
      mit.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mit.store_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (mit.store_code &&
        mit.store_code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="mit-checkin-container">
        <div className="mit-loading">
          <div className="mit-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mit-checkin-container">
      <div className="mit-content-wrapper">
        {/* Notification */}
        {notification.show && (
          <div className={`mit-notification notification-${notification.type}`}>
            {notification.message}
          </div>
        )}

        <div className="mit-header">
          <h1 className="mit-title">MIT Check-in Dashboard</h1>
          <p className="mit-subtitle">
            Track and complete weekly check-ins for Managers in Training
          </p>
        </div>

        {/* Search Bar */}
        <div className="mit-search-container">
          <Search className="mit-search-icon" />
          <input
            type="text"
            placeholder="Search by name, store, or code..."
            className="mit-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* MIT List */}
        <div className="mit-list">
          {filteredMITs.length === 0 ? (
            <div className="mit-empty-state">
              No MITs found matching your search criteria
            </div>
          ) : (
            filteredMITs.map((mit) => (
              <MITCard
                key={mit.mit_user_id}
                mit={mit}
                onSelectWeek={(week) => {
                  setSelectedMIT(mit);
                  setSelectedWeek(week);
                }}
                onLocationUpdate={() => fetchMITs()}
                onNotification={showNotification}
              />
            ))
          )}
        </div>

        {/* Check-in Form Modal */}
        {selectedMIT && selectedWeek && (
          <CheckinForm
            mit={selectedMIT}
            weekNumber={selectedWeek}
            onClose={() => {
              setSelectedMIT(null);
              setSelectedWeek(null);
              fetchMITs();
            }}
            onNotification={showNotification}
          />
        )}
      </div>
    </div>
  );
};

// MIT Card Component
const MITCard = ({ mit, onSelectWeek, onLocationUpdate, onNotification }) => {
  const [expanded, setExpanded] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await apiClient.get("/api/mit-checkin/locations");
      if (response.data.success) {
        setLocations(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      onNotification("Failed to load locations", "error");
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleLocationUpdate = async () => {
    if (!selectedLocation) {
      onNotification("Please select a location", "error");
      return;
    }

    setSavingLocation(true);
    try {
      const response = await apiClient.put(
        `/api/mit-checkin/mit/${mit.mit_user_id}/location`,
        { locationPc: selectedLocation }
      );

      if (response.data.success) {
        onNotification("Location updated successfully", "success");
        setShowLocationDropdown(false);
        onLocationUpdate();
      }
    } catch (error) {
      console.error("Error updating location:", error);
      onNotification("Failed to update location", "error");
    } finally {
      setSavingLocation(false);
    }
  };

  const weeks = Array.from({ length: 8 }, (_, i) => {
    const weekNum = i + 1;
    const startDate = addWeeks(new Date(mit.start_date), i);
    const checkin = mit.checkins.find((c) => c.week_number === weekNum);

    return {
      number: weekNum,
      startDate,
      checkin,
      status: getWeekStatus(startDate, checkin),
    };
  });

  const completedCount = mit.checkins.filter(
    (c) => c.status === "completed"
  ).length;
  const progress = (completedCount / 8) * 100;

  return (
    <div className="mit-card">
      <div className="mit-card-header" onClick={() => setExpanded(!expanded)}>
        <div className="mit-card-content">
          <div className="mit-info">
            <div className="mit-name-row">
              <h3 className="mit-name">{mit.full_name}</h3>
              <span
                className={`mit-status-badge status-${mit.current_status.replace(
                  "_",
                  "-"
                )}`}
              >
                {mit.current_status.replace("_", " ").toUpperCase()}
              </span>
            </div>
            {mit.needs_location && !showLocationDropdown ? (
              <div className="mit-location-required">
                <p className="mit-store-info">
                  {mit.store_name} - Location Required
                  {mit.corp && ` (${mit.corp})`}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchLocations();
                    setShowLocationDropdown(true);
                  }}
                  className="mit-assign-location-btn"
                >
                  <MapPin className="h-4 w-4" />
                  Assign Location
                </button>
              </div>
            ) : (
              <p className="mit-store-info">
                {mit.store_name} {mit.store_code && `(${mit.store_code})`}
                {mit.market && ` - ${mit.market}`}
              </p>
            )}
            <p className="mit-start-date">
              Started: {formatDate(mit.start_date, "MMM d, yyyy")}
            </p>
          </div>
          <ChevronRight
            className={`mit-expand-icon ${expanded ? "expanded" : ""}`}
          />
        </div>

        {/* Progress Bar */}
        <div className="mit-progress-section">
          <div className="mit-progress-info">
            <span>{completedCount} of 8 weeks completed</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="mit-progress-bar">
            <div
              className={`mit-progress-fill ${
                progress === 100
                  ? "progress-high"
                  : progress >= 75
                  ? "progress-medium"
                  : progress >= 50
                  ? "progress-low"
                  : "progress-0"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Location Dropdown */}
      {showLocationDropdown && (
        <div
          className="mit-location-dropdown"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mit-location-dropdown-header">
            <h4>Select Location for {mit.full_name}</h4>
            <button
              onClick={() => setShowLocationDropdown(false)}
              className="mit-location-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {loadingLocations ? (
            <div className="mit-location-loading">Loading locations...</div>
          ) : (
            <>
              <div className="mit-location-select-wrapper">
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="mit-location-select"
                >
                  <option value="">Select a location...</option>
                  {locations.map((loc) => (
                    <option key={loc.location_pc} value={loc.location_pc}>
                      {loc.location_pc} - {loc.corp}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mit-location-actions">
                <button
                  onClick={() => setShowLocationDropdown(false)}
                  className="mit-btn mit-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLocationUpdate}
                  disabled={!selectedLocation || savingLocation}
                  className="mit-btn mit-btn-primary"
                >
                  {savingLocation ? "Saving..." : "Save Location"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Expanded Week View */}
      {expanded && (
        <div className="mit-weeks-container">
          <div className="mit-weeks-grid">
            {weeks.map((week) => (
              <WeekCard
                key={week.number}
                week={week}
                onClick={() =>
                  week.status !== "completed" && onSelectWeek(week.number)
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Week Card Component
const WeekCard = ({ week, onClick }) => {
  const status =
    week.checkin?.status === "rescheduled" ? "rescheduled" : week.status;

  return (
    <button
      onClick={onClick}
      className={`week-card week-${status}`}
      disabled={week.status === "completed"}
    >
      <div className="week-card-header">
        <span className="week-number">Week {week.number}</span>
        {status === "completed" && <Check className="week-icon" />}
        {status === "overdue" && <AlertCircle className="week-icon" />}
        {status === "due_today" && <Clock className="week-icon" />}
        {(status === "upcoming" || status === "rescheduled") && (
          <Calendar className="week-icon" />
        )}
      </div>
      <div className="week-date">
        {formatDate(week.startDate, "MMM d")}
        {week.checkin?.status === "rescheduled" &&
          week.checkin?.scheduled_date && (
            <div className="week-reschedule-date">
              → {formatDate(week.checkin.scheduled_date, "MMM d")}
            </div>
          )}
      </div>
    </button>
  );
};

// Check-in Form Component
const CheckinForm = ({ mit, weekNumber, onClose, onNotification }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkinData, setCheckinData] = useState(null);
  const [responses, setResponses] = useState({});
  const [trmName, setTrmName] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [requiresLocation, setRequiresLocation] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const mitSignatureRef = useRef(null);
  const trmSignatureRef = useRef(null);

  useEffect(() => {
    fetchCheckinDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mit.mit_user_id, weekNumber]);

  const fetchCheckinDetails = async () => {
    try {
      const response = await apiClient.get(
        `/api/mit-checkin/checkin/${mit.mit_user_id}/${weekNumber}`
      );

      if (response.data.requiresLocation) {
        setRequiresLocation(true);
        setLocations(response.data.availableLocations);
        setLoading(false);
        return;
      }

      if (response.data.success) {
        const data = response.data.data;
        setCheckinData(data);
        setTrmName(data.trm_name || "");

        // Initialize responses
        const initialResponses = {};
        data.questions.forEach((q) => {
          initialResponses[q.question_id] = q.response_value || "";
        });
        setResponses(initialResponses);
      }
    } catch (error) {
      console.error("Error fetching checkin details:", error);
      onNotification("Failed to load check-in details", "error");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSubmit = async () => {
    if (!selectedLocation) {
      onNotification("Please select a location", "error");
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.put(
        `/api/mit-checkin/mit/${mit.mit_user_id}/location`,
        { locationPc: selectedLocation }
      );

      if (response.data.success) {
        onNotification("Location updated successfully", "success");
        setRequiresLocation(false);
        setLoading(true);

        // Add a small delay to ensure database consistency
        setTimeout(() => {
          fetchCheckinDetails();
        }, 500);
      }
    } catch (error) {
      console.error("Error updating location:", error);
      onNotification("Failed to update location", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);

    // Validate required fields
    const unanswered = checkinData.questions.filter(
      (q) => q.is_required && !responses[q.question_id]
    );

    if (unanswered.length > 0) {
      onNotification("Please answer all required questions", "error");
      setSaving(false);
      return;
    }

    if (!trmName.trim()) {
      onNotification("Please enter the TRM name", "error");
      setSaving(false);
      return;
    }

    // Get signatures
    const mitSignature = mitSignatureRef.current?.toDataURL();
    const trmSignature = trmSignatureRef.current?.toDataURL();

    if (!mitSignature || !trmSignature) {
      onNotification("Both signatures are required", "error");
      setSaving(false);
      return;
    }

    try {
      const payload = {
        responses: Object.entries(responses).map(([questionId, value]) => ({
          questionId: parseInt(questionId),
          value: value,
        })),
        trmName,
        mitSignature,
        trmSignature,
      };

      const response = await apiClient.post(
        `/api/mit-checkin/checkin/${checkinData.session_id}`,
        payload
      );

      if (response.data.success) {
        onNotification("Check-in completed successfully!", "success");
        onClose();
      } else {
        throw new Error(response.data.message || "Failed to save check-in");
      }
    } catch (error) {
      console.error("Error saving checkin:", error);
      onNotification(
        error.response?.data?.message || "Error saving check-in",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mit-modal-overlay">
        <div className="mit-modal">
          <div className="mit-loading">
            <div className="mit-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (requiresLocation) {
    return (
      <div className="mit-modal-overlay">
        <div className="mit-modal">
          <div className="mit-modal-header">
            <div>
              <h2 className="mit-modal-title">Location Required</h2>
              <p className="mit-modal-subtitle">
                Please select a location for {mit.full_name} before starting
                Week 1 check-in
              </p>
            </div>
            <button onClick={onClose} className="mit-modal-close">
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="mit-modal-body">
            <div className="mit-form-group">
              <label className="mit-form-label">
                Select Location <span className="mit-required">*</span>
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="mit-form-select"
              >
                <option value="">Select a location...</option>
                {locations.map((loc) => (
                  <option key={loc.location_pc} value={loc.location_pc}>
                    {loc.location_pc} - {loc.corp}
                  </option>
                ))}
              </select>
            </div>

            <div className="mit-form-buttons">
              <button onClick={onClose} className="mit-btn mit-btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleLocationSubmit}
                disabled={!selectedLocation || saving}
                className="mit-btn mit-btn-primary"
              >
                {saving ? "Saving..." : "Continue to Check-in"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mit-modal-overlay">
      <div className="mit-modal">
        {/* Header */}
        <div className="mit-modal-header">
          <div>
            <h2 className="mit-modal-title">Week {weekNumber} Check-in</h2>
            <p className="mit-modal-subtitle">
              {mit.full_name} - {mit.store_name}
            </p>
            {checkinData?.scheduled_date && (
              <p className="mit-modal-date">
                Scheduled:{" "}
                {formatDate(checkinData.scheduled_date, "MMM d, yyyy")}
              </p>
            )}
          </div>
          <button onClick={onClose} className="mit-modal-close">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="mit-modal-body">
          {/* Action Buttons */}
          {checkinData?.status !== "completed" && (
            <div className="mit-action-buttons">
              <button
                onClick={() => setShowReschedule(true)}
                className="mit-reschedule-btn"
              >
                Reschedule
              </button>
            </div>
          )}

          {/* TRM Name */}
          <div className="mit-form-group">
            <label className="mit-form-label">
              Training Restaurant Manager (TRM) Name{" "}
              <span className="mit-required">*</span>
            </label>
            <input
              type="text"
              value={trmName}
              onChange={(e) => setTrmName(e.target.value)}
              className="mit-form-input"
              placeholder="Enter TRM name..."
              disabled={checkinData?.status === "completed"}
            />
          </div>

          {/* Questions */}
          <div className="mit-questions-list">
            {checkinData?.questions.map((question) => (
              <QuestionField
                key={question.question_id}
                question={question}
                value={responses[question.question_id]}
                onChange={(value) =>
                  setResponses({
                    ...responses,
                    [question.question_id]: value,
                  })
                }
                disabled={checkinData?.status === "completed"}
              />
            ))}
          </div>

          {/* Signatures */}
          {checkinData?.status !== "completed" && (
            <div className="mit-signatures-grid">
              <SignatureField
                label="MIT Signature"
                ref={mitSignatureRef}
                name={mit.full_name}
              />
              <SignatureField
                label="TRM Signature"
                ref={trmSignatureRef}
                name={trmName || "Training Restaurant Manager"}
              />
            </div>
          )}

          {/* Submit Button */}
          {checkinData?.status !== "completed" && (
            <div className="mit-form-buttons">
              <button onClick={onClose} className="mit-btn mit-btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="mit-btn mit-btn-primary"
              >
                {saving ? "Saving..." : "Complete Check-in"}
              </button>
            </div>
          )}

          {/* Completed Status */}
          {checkinData?.status === "completed" && (
            <div className="mit-completed-status">
              <p>
                This check-in was completed on{" "}
                {formatDate(checkinData.completed_date, "MMM d, yyyy")}
              </p>
            </div>
          )}
        </div>

        {/* Reschedule Modal */}
        {showReschedule && (
          <RescheduleModal
            sessionId={checkinData.session_id}
            onClose={() => {
              setShowReschedule(false);
              onClose();
            }}
            onNotification={onNotification}
          />
        )}
      </div>
    </div>
  );
};

// Question Field Component
const QuestionField = ({ question, value, onChange, disabled }) => {
  return (
    <div className="mit-form-group">
      <label className="mit-form-label">
        {question.question_text}
        {question.is_required && <span className="mit-required">*</span>}
      </label>

      {question.question_type === "yes_no" ? (
        <div className="mit-radio-group">
          <label className="mit-radio-label">
            <input
              type="radio"
              name={`question-${question.question_id}`}
              value="yes"
              checked={value === "yes"}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
            />
            Yes
          </label>
          <label className="mit-radio-label">
            <input
              type="radio"
              name={`question-${question.question_id}`}
              value="no"
              checked={value === "no"}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
            />
            No
          </label>
        </div>
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="mit-form-textarea"
          placeholder="Enter your response..."
          disabled={disabled}
        />
      )}
    </div>
  );
};

// Signature Field Component
const SignatureField = React.forwardRef(({ label, name }, ref) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = 150;

    // Draw signature line
    drawSignatureLine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const drawSignatureLine = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(10, 140);
    ctx.lineTo(canvas.width - 10, 140);
    ctx.strokeStyle = "#ccc";
    ctx.stroke();
    ctx.strokeStyle = "#000";
  };

  React.useImperativeHandle(ref, () => ({
    toDataURL: () => (hasSignature ? canvasRef.current.toDataURL() : null),
    clear: () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawSignatureLine();
      setHasSignature(false);
    },
  }));

  const getCoordinates = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    setHasSignature(true);

    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;

    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawSignatureLine();
    setHasSignature(false);
  };

  return (
    <div className="mit-form-group">
      <label className="mit-form-label">
        {label} <span className="mit-required">*</span>
      </label>
      <div className="mit-signature-container">
        <canvas
          ref={canvasRef}
          className="mit-signature-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="mit-signature-footer">
          <span className="mit-signature-name">{name}</span>
          <button
            type="button"
            onClick={clearSignature}
            className="mit-signature-clear"
          >
            Clear Signature
          </button>
        </div>
      </div>
    </div>
  );
});

SignatureField.displayName = "SignatureField";

// Reschedule Modal Component
const RescheduleModal = ({ sessionId, onClose, onNotification }) => {
  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [newDate, setNewDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRescheduleReasons();
  }, []);

  const fetchRescheduleReasons = async () => {
    try {
      const response = await apiClient.get(
        "/api/mit-checkin/reschedule-reasons"
      );
      if (response.data.success) {
        setReasons(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching reschedule reasons:", error);
    }
  };

  const handleReschedule = async () => {
    if (!selectedReason || !newDate) {
      onNotification("Please select a reason and new date", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.put(
        `/api/mit-checkin/checkin/${sessionId}/reschedule`,
        {
          reasonId: parseInt(selectedReason),
          newDate,
          notes,
        }
      );

      if (response.data.success) {
        onNotification("Check-in rescheduled successfully!", "success");
        onClose();
      } else {
        throw new Error(response.data.message || "Failed to reschedule");
      }
    } catch (error) {
      console.error("Error rescheduling:", error);
      onNotification(
        error.response?.data?.message || "Error rescheduling check-in",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mit-reschedule-modal">
      <div className="mit-reschedule-content">
        <h3 className="mit-reschedule-title">Reschedule Check-in</h3>

        <div className="mit-form-group">
          <label className="mit-form-label">
            Reason for Rescheduling <span className="mit-required">*</span>
          </label>
          <select
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
            className="mit-form-select"
          >
            <option value="">Select a reason...</option>
            {reasons.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.reason}
              </option>
            ))}
          </select>
        </div>

        <div className="mit-form-group">
          <label className="mit-form-label">
            New Date <span className="mit-required">*</span>
          </label>
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            min={formatDate(new Date(), "yyyy-MM-dd")}
            className="mit-form-input"
          />
        </div>

        <div className="mit-form-group">
          <label className="mit-form-label">Additional Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mit-form-textarea"
            placeholder="Optional notes..."
          />
        </div>

        <div className="mit-form-buttons">
          <button onClick={onClose} className="mit-btn mit-btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleReschedule}
            disabled={loading}
            className="mit-btn mit-btn-primary"
          >
            {loading ? "Rescheduling..." : "Reschedule"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Export the main component
export default MITCheckinDashboard;